import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import type { Theme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { FC } from "react";
import { Outlet } from "react-router";

import { ActivityProvider } from "src/activity/ActivityProvider";
import { RouteActivity } from "src/activity/RouteActivity";
import { AdvisorColumn } from "src/advisor/AdvisorColumn";
import { AdvisorProvider, useAdvisor } from "src/advisor/AdvisorProvider";
import { AdvisorScreen } from "src/advisor/AdvisorScreen";
import { useOrganizationLink } from "src/auth/useOrganizationLink";
import { useSession } from "src/auth/useSession";
import { AppTopBar } from "src/components/AppShell/AppTopBar";
import { BottomTabs } from "src/components/AppShell/BottomTabs";
import {
	BOTTOM_TABS_HEIGHT,
	CONTENT_MAX_WIDTH,
	SAFE_AREA_BOTTOM,
	TOP_BAR_HEIGHT
} from "src/components/AppShell/layout";
import { SideNav } from "src/components/AppShell/SideNav";
import { ShellProvider, useIsFullBleed, useIsFullWidth } from "src/components/AppShell/shellContext";
import { useAdvisorShortcut } from "src/components/AppShell/useAdvisorShortcut";
import { SignInPage } from "src/pages/SignInPage/SignInPage";

/** Mobile pages scroll clear of the fixed tab bar. */
const MOBILE_BOTTOM_PADDING = `calc(${BOTTOM_TABS_HEIGHT}px + ${SAFE_AREA_BOTTOM} + 28px)`;
const isDesktopQuery = (theme: Theme) => theme.breakpoints.up("md");

/**
 * Figma Desktop v3 / Mobile v3 layout. From `md` up: top bar, then the left slot holds either the
 * side nav or, when open, the AI Advisor column (D12 / D14b), then the page. Below `md`: header,
 * page, bottom tab bar, and the advisor as a full-screen view (M12).
 */
const ShellLayout: FC = () => {
	const { open, toggleAdvisor } = useAdvisor();
	const isDesktop = useMediaQuery(isDesktopQuery, { noSsr: true });
	const fullBleed = useIsFullBleed();
	const fullWidth = useIsFullWidth();
	useAdvisorShortcut(toggleAdvisor);

	return (
		<>
			<RouteActivity />
			<Box sx={{ minHeight: "100dvh", display: "flex", flexDirection: "column" }}>
				<AppTopBar />
				<Box sx={{ flex: 1, display: "flex" }}>
					{isDesktop && open ? <AdvisorColumn /> : <SideNav />}
					{fullBleed ? (
						// Maps: the page owns the whole area under the top bar (and above the mobile tab bar).
						<Box
							component="main"
							sx={{
								flex: 1,
								minWidth: 0,
								position: "relative",
								height: {
									xs: `calc(100dvh - ${TOP_BAR_HEIGHT.xs}px - ${BOTTOM_TABS_HEIGHT}px - ${SAFE_AREA_BOTTOM})`,
									md: `calc(100dvh - ${TOP_BAR_HEIGHT.md}px)`
								},
								overflow: "hidden",
								containerType: "inline-size"
							}}>
							<Outlet />
						</Box>
					) : (
						<Box
							component="main"
							sx={{
								flex: 1,
								minWidth: 0,
								// D14b: 24px beside the advisor column, 48px beside the nav.
								px: { xs: 2, md: 3, lg: isDesktop && open ? 3 : 6 },
								pt: { xs: 2.5, md: 5 },
								pb: { xs: MOBILE_BOTTOM_PADDING, md: 6 }
							}}>
							{/* A size container, so page layouts respond to the space left beside the advisor. */}
							<Box sx={{ maxWidth: fullWidth ? "none" : CONTENT_MAX_WIDTH, containerType: "inline-size" }}>
								<Outlet />
							</Box>
						</Box>
					)}
				</Box>
				<BottomTabs />
			</Box>
			<AdvisorScreen open={!isDesktop && open} />
		</>
	);
};

/** The signed-in shell. Signed-out visitors get the sign-in page; activity is only captured inside it. */
export const AppShell: FC = () => {
	const { isLoading, user } = useSession();
	const switchingOrganization = useOrganizationLink();

	if (isLoading || switchingOrganization) {
		return (
			<Box sx={{ display: "grid", placeItems: "center", minHeight: "100dvh" }}>
				<CircularProgress />
			</Box>
		);
	}
	if (!user) return <SignInPage />;

	return (
		<ActivityProvider>
			<AdvisorProvider>
				<ShellProvider>
					<ShellLayout />
				</ShellProvider>
			</AdvisorProvider>
		</ActivityProvider>
	);
};
